import React, { useRef, useEffect } from 'react';
import styled from 'astroturf/react';

const SidebarContainer = styled.div`
  @import 'stylesheets/header.scss';

  position: sticky;
  top: 0;
  padding-top: 16px;
  padding-bottom: var(--nav-mobile-bottom-height);

  @include for-tablet-up {
    padding-bottom: 0;
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

type Props = {
  className?: string;
  children: React.ReactNode;
};

export const StickySidebar: React.FC<Props> = ({ className, children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastScrollPos = useRef(window.scrollY);

  const handleWindowScroll = () => {
    const container = containerRef.current;
    if (!container || !container.parentElement) return;

    const scrollY = window.scrollY;
    const viewportHeight = window.innerHeight;
    const { top, bottom, height } = container.getBoundingClientRect();
    // This is how far down the page the sidebar is initially. It's usually 80px for the nav bar,
    // sometimes more if there is a banner at the top of the page.
    const initialOffset = container.parentElement.offsetTop;
    // As the user scrolls up and down, the top of the sidebar can drift from the initial offset.
    // This calculates the space between the current top of the sidebar and where it started, to be
    // used when the container is in relative position.
    const currentOffset = container.offsetTop - initialOffset;

    // Early exit condition: sidebar is short enough to fit in viewport
    if (height < viewportHeight) return;

    // Helper function for updating container style
    const updateContainerStyle = (position: string, top: number) => {
      container.style.top = `${top}px`;
      container.style.position = position;
    };

    if (top < 0 && bottom > viewportHeight) {
      // The top and bottom ends of the sidebar are above and below the viewport, respectively
      updateContainerStyle('relative', currentOffset);
    } else if (scrollY > lastScrollPos.current) {
      // Scrolling down
      if (top >= 0 && top <= viewportHeight) {
        // If top of sidebar is visible, make sidebar scrollable
        updateContainerStyle('relative', currentOffset);
      } else if (bottom <= viewportHeight) {
        // If bottom of sidebar is visible or above the viewport, make sidebar sticky on bottom
        updateContainerStyle('sticky', viewportHeight - height);
      }
    } else if (scrollY < lastScrollPos.current) {
      // Scrolling up
      if (top >= 0) {
        // If top of sidebar is visible or below the viewport, make sidebar sticky on top
        updateContainerStyle('sticky', 0);
      } else if (bottom >= 0 && bottom <= viewportHeight) {
        // If bottom of sidebar is visible, make sidebar scrollable
        updateContainerStyle('relative', currentOffset);
      }
    }
    lastScrollPos.current = scrollY;
  };

  useEffect(() => {
    const throttle = (fn: () => void, delay: number) => {
      // set current time
      let time = Date.now();

      return () => {
        if (time + delay - Date.now() <= 0) {
          // run function & reset time
          fn();
          time = Date.now();
        }
      };
    };

    const throttledHandleWindowScroll = throttle(handleWindowScroll, 20);

    window.addEventListener('scroll', throttledHandleWindowScroll);
    return () => {
      window.removeEventListener('scroll', throttledHandleWindowScroll);
    };
  }, []);

  // Run handleWindowScroll on initial render
  handleWindowScroll();

  return (
    <SidebarContainer ref={containerRef} className={className}>
      <SidebarContent>{children}</SidebarContent>
    </SidebarContainer>
  );
};
